import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../LoadingSpinner';
import MirrorNotice from './MirrorNotice';
import './Operations.css';
import './LowStockDashboard.css';

function ReorderSuggestionsDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('days_of_supply');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get('/api/inventory/reorder-suggestions');
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load reorder suggestions');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'sku' || key === 'name' ? 'asc' : 'asc'); }
  };

  const filteredItems = useMemo(() => {
    if (!data) return [];
    const q = search.toLowerCase();
    const items = (data.items || []).filter(
      (item) => !q || item.sku.toLowerCase().includes(q) || item.name.toLowerCase().includes(q)
    );
    return [...items].sort((a, b) => {
      const av = a[sortKey] ?? 0;
      const bv = b[sortKey] ?? 0;
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? av - bv : bv - av;
    });
  }, [data, search, sortKey, sortDir]);

  if (loading) return <div className="ops-page"><LoadingSpinner /></div>;

  if (error) return (
    <div className="ops-page">
      <div className="ops-page-header">
        <button className="back-button" onClick={() => navigate('/ops')}>← Back</button>
        <h1>Reorder Suggestions</h1>
      </div>
      <div className="message-box error">{error}</div>
    </div>
  );

  const asOf = data?.as_of ? new Date(data.as_of).toLocaleString() : '';

  return (
    <div className="ops-page low-stock-page">
      <div className="ops-page-header">
        <button className="back-button" onClick={() => navigate('/ops')}>← Back</button>
        <div className="low-stock-title-block">
          <h1>Reorder Suggestions</h1>
          {asOf && <span className="low-stock-as-of">as of {asOf}</span>}
        </div>
        <button className="low-stock-refresh" onClick={fetchData}>Refresh</button>
      </div>

      <MirrorNotice data={data} />

      <p className="slow-mover-note">
        Raw materials and components (vessels, oils, wax, wicks, labels, packaging) for Rewined + Candlefish
        finished goods, projected from on-hand at Rewined warehouses vs. the consumption rate implied by each
        finished good's {data?.lookback_days || 90}-day sell velocity. Flags anything under {data?.reorder_threshold_days || 30}{' '}
        days of supply; suggested quantity brings it up to {data?.target_coverage_days || 60} days.
        Does not account for quantities already on open purchase orders — check NetSuite before ordering.
      </p>

      <div className="low-stock-summary-row">
        <div className="low-stock-summary-chip low-stock-summary-total">
          <span className="low-stock-summary-brand">Needs Reorder</span>
          <span className="low-stock-summary-count">{filteredItems.length}</span>
        </div>
      </div>

      <div className="low-stock-controls">
        <input
          className="low-stock-search"
          type="text"
          placeholder="Search SKU or name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {filteredItems.length === 0 ? (
        <div className="no-data">Nothing below the reorder threshold right now.</div>
      ) : (
        <div className="low-stock-brand-section">
          <div className="low-stock-table-wrap">
            <table className="low-stock-table">
              <thead>
                <tr>
                  <SortHeader label="SKU" col="sku" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                  <SortHeader label="Name" col="name" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                  <SortHeader label="On Hand" col="on_hand" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                  <SortHeader label="Daily Use" col="daily_consumption" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                  <SortHeader label="Days of Supply" col="days_of_supply" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                  <SortHeader label="Suggested Reorder Qty" col="suggested_qty" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => {
                  const critical = item.days_of_supply <= 0;
                  return (
                    <tr
                      key={item.sku}
                      className={`low-stock-row ${critical ? 'low-stock-critical' : 'low-stock-warning'}`}
                    >
                      <td className="low-stock-sku">
                        {item.ns_url
                          ? <a href={item.ns_url} target="_blank" rel="noreferrer" className="low-stock-ns-link">{item.sku}</a>
                          : item.sku}
                      </td>
                      <td className="low-stock-name">{item.name}</td>
                      <td className="low-stock-num">{item.on_hand} {item.unit}</td>
                      <td className="low-stock-num">{item.daily_consumption} {item.unit}/day</td>
                      <td className="low-stock-num low-stock-deficit">{item.days_of_supply}</td>
                      <td className="low-stock-num" style={{ fontWeight: 700 }}>{item.suggested_qty} {item.unit}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function SortHeader({ label, col, sortKey, sortDir, onSort, align }) {
  const active = sortKey === col;
  return (
    <th
      className={`low-stock-th ${align === 'right' ? 'low-stock-th-right' : ''} ${active ? 'low-stock-th-active' : ''}`}
      onClick={() => onSort(col)}
    >
      {label}
      {active && <span className="low-stock-sort-arrow">{sortDir === 'asc' ? ' ↑' : ' ↓'}</span>}
    </th>
  );
}

export default ReorderSuggestionsDashboard;
